import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { baseQuery } from '../../../utils';
import { getCodeTranslation } from '../../../graphql';
import type { GetCompanyCodeTranslationQueryVariables, GetCodeTranslationQuery } from '../../../graphql';
import type { Options } from '../types';
import { RTK_CACHE_LIFETIME } from '../../../config';

// ----------------------
// Country + State API
// ----------------------
export const locationApi = createApi({
  reducerPath: 'locationApi',
  baseQuery,
  tagTypes: ['locationData'],
  endpoints: (builder) => ({
    getCountryAndStateOptions: builder.query<Options[], GetCompanyCodeTranslationQueryVariables>({
      query: (variables) => ({
        body: getCodeTranslation,
        variables,
      }),
      keepUnusedDataFor: RTK_CACHE_LIFETIME,
      providesTags: ['locationData'],

      transformResponse: (response: GetCodeTranslationQuery, _meta, variables: GetCompanyCodeTranslationQueryVariables) => {
        if (variables?.codetype === 'Country') {
          return (
            response?.getCodeTranslation?.map((item) => ({
              label: item?.codetext || '',
              value: `${item?.codetext}State` || '',
              codeValue: item?.codevalue,
            })) || []
          );
        } else if (variables?.codetype === 'IndiaState') {
          return (
            response?.getCodeTranslation?.map((item) => ({
              label: item?.codetext || '',
              value: `${item?.codetext}District` || '',
              code: item?.statecode,
            })) || []
          );
        }
        return [];
      },
    }),
  }),
});

// ----------------------
// District API (separate, short cache + 5-state limit)
// ----------------------

interface StateCache {
  stateName: string;
  data: Options[];
  timestamp: number;
}

class DistrictCache {
  private cache: StateCache[] = [];
  private readonly maxSize = 5;

  add(stateName: string, data: Options[]) {
    this.cache = this.cache.filter((c) => c.stateName !== stateName);
    this.cache.unshift({ stateName, data, timestamp: Date.now() });
    if (this.cache.length > this.maxSize) this.cache = this.cache.slice(0, this.maxSize);
  }

  get(stateName: string) {
    return this.cache.find((c) => c.stateName === stateName)?.data;
  }

  getAllStateNames() {
    return this.cache.map((c) => c.stateName);
  }
}

const districtCache = new DistrictCache();

export const districtApi = createApi({
  reducerPath: 'districtApi',
  baseQuery,
  tagTypes: ['districtData'],
  endpoints: (builder) => ({
    getDistrictOptions: builder.query<Options[], { codetype: string }>({
      async queryFn({ codetype }) {
        const cached = districtCache.get(codetype);
        if (cached?.length) {
          console.log(`🟩 Using cached districts for ${codetype}`);
          return { data: cached };
        }

        const response: any = await baseQuery({
          body: getCodeTranslation,
          variables: { codetype: `${codetype}` },
        });

        const data =
          response?.data?.getCodeTranslation?.map((item: any) => ({
            label: item?.codetext || '',
            value: `${item?.codetext}District` || '',
          })) || [];

        districtCache.add(codetype, data);
        console.log(`🟨 Added district cache for ${codetype}`, districtCache.getAllStateNames());
        return { data };
      },
      keepUnusedDataFor: 0, // short-lived
      providesTags: ['districtData'],
    }),
  }),
});

export const { useGetCountryAndStateOptionsQuery, useLazyGetCountryAndStateOptionsQuery } = locationApi;

export const { useGetDistrictOptionsQuery, useLazyGetDistrictOptionsQuery } = districtApi;
